/**
 * Quality Management System (QMS) Type Definitions
 * Following Clean Architecture and FDA 21 CFR Part 11 Compliance
 */

import { UserRoleEnum, normalizeRole } from "./role.type";
import {
  QMS_STATUS,
  QMS_STATUS_VALUES,
  type QmsStatusValue,
} from "./riskStatus.type";

// Re-export for convenience
export { UserRoleEnum };

// ─── QMS Record Types ────────────────────────────────────────────────────────

export type QMSRecordType =
  | "Issue"
  | "Risk"
  | "Query Escalation"
  | "Safety Follow-up"
  | "Protocol Deviation"
  | "CAPA"
  | "Monitoring Finding"
  | "Missing Document Alert"
  | "Regulatory Delay"
  | "Site Performance Concern";

export type QMSSource = "Manual" | "EDC" | "eTMF" | "System";
export type QMSSeverity = "Low" | "Medium" | "High" | "Critical";
/** Create/update priority accepted by the API (Critical severity maps to High). */
export type QMSPriority = "Low" | "Medium" | "High";
export type QMSStatus = QmsStatusValue;

export const QMS_PRIORITIES: QMSPriority[] = ["Low", "Medium", "High"];

/**
 * Backend create DTO requires `priority` (Low|Medium|High). The UI still uses
 * severity including Critical; Critical collapses to High for the API field.
 */
export const toQmsPriority = (severity: QMSSeverity): QMSPriority =>
  severity === "Critical" ? "High" : severity;

/**
 * Normalize role string to UserRoleEnum using centralized normalizeRole
 * Ensures consistency with application-wide role handling
 */
const normalizeQmsRole = (role: string): UserRoleEnum => {
  return normalizeRole(role);
};

export const QMS_RECORD_TYPES: QMSRecordType[] = [
  "Issue",
  "Risk",
  "Query Escalation",
  "Safety Follow-up",
  "Protocol Deviation",
  "CAPA",
  "Monitoring Finding",
  "Missing Document Alert",
  "Regulatory Delay",
  "Site Performance Concern",
];

export const QMS_SOURCES: QMSSource[] = ["Manual", "EDC", "eTMF", "System"];
export const QMS_SEVERITIES: QMSSeverity[] = [
  "Low",
  "Medium",
  "High",
  "Critical",
];
export const QMS_STATUSES: QMSStatus[] = [...QMS_STATUS_VALUES];
export { QMS_STATUS };

// ─── CAPA Record ─────────────────────────────────────────────────────────────

export interface CAPARecord {
  correctiveAction: string;
  preventiveAction: string;
  capaOwner: string;
  capaOwnerInitials: string;
  capaDueDate: string;
  effectivenessCheck: string;
  effectivenessCheckDate: string;
  effectivenessApproved: boolean;
  approvedBy?: string;
  approvedDate?: string;
}

// ─── Comment & History ───────────────────────────────────────────────────────

export interface QMSComment {
  id: string;
  text: string;
  author: string;
  authorInitials: string;
  authorRole: string;
  timestamp: string;
}

export interface QMSHistoryEntry {
  id: string;
  action: string;
  user: string;
  userInitials: string;
  userRole: string;
  timestamp: string;
  note?: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
}

// ─── QMS Record ──────────────────────────────────────────────────────────────

export interface QMSRecord {
  id: string;
  /** Human-readable code when API provides it (e.g. QMS-001). */
  recordCode?: string;
  type: QMSRecordType;
  title: string;
  description: string;
  study: string;
  studyId: string;
  site: string;
  siteId: string | null;
  severity: QMSSeverity;
  source: QMSSource;
  owner: string;
  ownerId: string;
  ownerInitials: string;
  ownerRole: string;
  status: QMSStatus;
  createdDate: string;
  dueDate: string;
  closedDate?: string | null;
  // Investigation
  rootCause?: string;
  investigationNotes?: string;
  investigatedBy?: string;
  investigationDate?: string;
  // CAPA
  capa?: CAPARecord;
  // Comments
  comments: QMSComment[];
  // History / Audit Trail
  history: QMSHistoryEntry[];
  // Auto-trigger metadata
  autoTriggered?: boolean;
  triggerSource?: string;
}

// ─── API Types (camelCase from backend) ──────────────────────────────────────

export interface ApiQMSRecord {
  id: string;
  recordCode?: string;
  type: QMSRecordType;
  title: string;
  description: string;
  studyId: string;
  siteId: string | null;
  severity: QMSSeverity;
  source: QMSSource;
  ownerId: string;
  status: QMSStatus;
  createdDate: string;
  dueDate: string;
  closedDate?: string | null;
  rootCause?: string;
  investigationNotes?: string;
  investigatedById?: string;
  investigationDate?: string;
  capa?: CAPARecord;
  comments?: QMSComment[];
  history?: QMSHistoryEntry[];
  autoTriggered?: boolean;
  triggerSource?: string;
}

// ─── Request/Response Types ──────────────────────────────────────────────────

export interface CreateQMSRecordRequest {
  type: QMSRecordType;
  title: string;
  description: string;
  studyId: string;
  siteId?: string;
  severity: QMSSeverity;
  /** Required by API validation — Low | Medium | High */
  priority: QMSPriority;
  source: QMSSource;
  ownerId: string;
  dueDate: string;
}

export interface UpdateQMSRecordRequest {
  title?: string;
  description?: string;
  severity?: QMSSeverity;
  priority?: QMSPriority;
  ownerId?: string;
  dueDate?: string;
  status?: QMSStatus;
  rootCause?: string;
  investigationNotes?: string;
  investigatedById?: string;
  investigationDate?: string;
}

export interface QMSListParams {
  page?: number;
  limit?: number;
  type?: QMSRecordType;
  status?: QMSStatus;
  severity?: QMSSeverity;
  source?: QMSSource;
  siteId?: string;
  ownerId?: string;
  /** When true, return only open records past due date (recommended API filter). */
  overdue?: boolean;
}

export interface QMSListResponse {
  items: QMSRecord[];
  total: number;
  page: number;
  limit: number;
}

export interface QMSDashboardData {
  totalRecords: number;
  openRecords: number;
  inProgressRecords: number;
  pendingReviewRecords: number;
  closedRecords: number;
  criticalRecords: number;
  highSeverityRecords: number;
  overdueRecords: number;
  byType: Record<QMSRecordType, number>;
  bySeverity: Record<QMSSeverity, number>;
  byStatus: Record<QMSStatus, number>;
  bySource: Record<QMSSource, number>;
  recentRecords: QMSRecord[];
}

// ─── RBAC Helper Functions ───────────────────────────────────────────────────

const FULL_ACCESS_ROLES: readonly UserRoleEnum[] = [
  UserRoleEnum.PROJECT_MANAGER,
  UserRoleEnum.DATA_MANAGER,
  UserRoleEnum.QA_QC,
] as const;

const CREATE_ROLES: readonly UserRoleEnum[] = [
  UserRoleEnum.PROJECT_MANAGER,
  UserRoleEnum.DATA_MANAGER,
  UserRoleEnum.QA_QC,
  UserRoleEnum.CRA,
  UserRoleEnum.CTA,
] as const;

const DELETE_ROLES: readonly UserRoleEnum[] = [
  UserRoleEnum.PROJECT_MANAGER,
  UserRoleEnum.QA_QC,
] as const;

export const QMS_RBAC = {
  /**
   * Check if role has any access to QMS module
   */
  hasAccess: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return [
      UserRoleEnum.PROJECT_MANAGER,
      UserRoleEnum.DATA_MANAGER,
      UserRoleEnum.QA_QC,
      UserRoleEnum.CRA,
      UserRoleEnum.CTA,
      UserRoleEnum.SPONSOR,
      UserRoleEnum.PRINCIPAL_INVESTIGATOR,
      UserRoleEnum.STUDY_COORDINATOR,
    ].includes(normalizedRole);
  },

  /**
   * Check if role can create new QMS records
   * PM: Full create
   * CRA: Create assigned items
   * CTA: Create startup/site issues
   * DM: Create query escalations
   * QA/QC: Full create (especially deviations/CAPA)
   */
  canCreate: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return CREATE_ROLES.includes(normalizedRole);
  },

  /**
   * Check if role can edit specific record types
   * PM: Full edit
   * DM: Full edit
   * CRA: Edit monitoring findings
   * CTA: Edit startup/regulatory delays
   * QA/QC: Full edit (especially deviations/CAPA)
   * Site Users: Edit if assigned to them
   */
  canEdit: (
    role: string | UserRoleEnum,
    record?: QMSRecord,
    userId?: string,
  ): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    if (FULL_ACCESS_ROLES.includes(normalizedRole)) {
      return true;
    }
    if (
      normalizedRole === UserRoleEnum.CRA &&
      record?.type === "Monitoring Finding"
    ) {
      return true;
    }
    if (
      normalizedRole === UserRoleEnum.CTA &&
      ["Regulatory Delay", "Issue"].includes(record?.type ?? "")
    ) {
      return true;
    }
    // Site users (PI / Study Coordinator) can edit if assigned to them
    if (
      [
        UserRoleEnum.PRINCIPAL_INVESTIGATOR,
        UserRoleEnum.STUDY_COORDINATOR,
      ].includes(normalizedRole) &&
      userId &&
      record?.ownerId === userId
    ) {
      return true;
    }
    return false;
  },

  /**
   * Check if role can assign records to other users
   * PM: Full assign
   * DM: Full assign
   * QA/QC: Full assign
   */
  canAssign: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return FULL_ACCESS_ROLES.includes(normalizedRole);
  },

  /**
   * Check if role can delete QMS records
   * PM: Can delete
   * QA/QC: Can delete
   * All others: Cannot delete
   */
  canDelete: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return DELETE_ROLES.includes(normalizedRole);
  },

  /**
   * Check if role can close records
   * PM / QA/QC / DM: Full close
   * CRA: Close monitoring findings only
   */
  canClose: (role: string | UserRoleEnum, record?: QMSRecord): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    if (
      normalizedRole === UserRoleEnum.PROJECT_MANAGER ||
      normalizedRole === UserRoleEnum.QA_QC ||
      normalizedRole === UserRoleEnum.DATA_MANAGER
    ) {
      return true;
    }
    if (
      normalizedRole === UserRoleEnum.CRA &&
      record?.type === "Monitoring Finding"
    ) {
      return true;
    }
    return false;
  },

  /**
   * Check if role can perform investigations
   * QA/QC: Full investigation authority
   * PM: Full investigation authority
   * DM: Full investigation authority
   */
  canInvestigate: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return FULL_ACCESS_ROLES.includes(normalizedRole);
  },

  /**
   * Check if role can manage CAPA (create/edit corrective and preventive actions)
   * QA/QC: Full CAPA authority
   * PM: Full CAPA authority
   * DM: Full CAPA authority
   */
  canManageCapa: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return FULL_ACCESS_ROLES.includes(normalizedRole);
  },

  /**
   * Check if role can approve CAPA effectiveness
   * QA/QC: Exclusive approval authority
   */
  canApproveEffectiveness: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return normalizedRole === UserRoleEnum.QA_QC;
  },

  /**
   * Check if role can approve record closure (final approval)
   * PM: Can approve
   * DM: Can approve
   * QA/QC: Can approve
   */
  canApproveClosure: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return FULL_ACCESS_ROLES.includes(normalizedRole);
  },

  /**
   * Check if role is read-only (view but no actions)
   * Sponsor: Read only
   */
  isReadOnly: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return normalizedRole === UserRoleEnum.SPONSOR;
  },

  /**
   * Check if role has limited view (site-specific only)
   * PI: Limited to assigned sites
   * StudyCoordinator: Limited to assigned sites
   */
  isLimitedView: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    return (
      normalizedRole === UserRoleEnum.PRINCIPAL_INVESTIGATOR ||
      normalizedRole === UserRoleEnum.STUDY_COORDINATOR
    );
  },

  /**
   * Check if role can add comments
   * All QMS-access roles except TenantAdmin and Sponsor
   */
  canComment: (role: string | UserRoleEnum): boolean => {
    const normalizedRole = normalizeQmsRole(role);
    if (
      normalizedRole === UserRoleEnum.TENANT ||
      normalizedRole === UserRoleEnum.SPONSOR
    ) {
      return false;
    }
    return QMS_RBAC.hasAccess(normalizedRole);
  },

  /**
   * Get role label with access description
   */
  getRoleLabel: (role: string | UserRoleEnum): string => {
    const normalizedRole = normalizeQmsRole(role);
    const labels: Record<UserRoleEnum, string> = {
      [UserRoleEnum.PROJECT_MANAGER]: "PM — Full Access",
      [UserRoleEnum.DATA_MANAGER]: "Data Manager — Full Access",
      [UserRoleEnum.QA_QC]: "QA/QC — Quality Authority",
      [UserRoleEnum.CRA]: "CRA/Monitor — Monitoring",
      [UserRoleEnum.CTA]: "CTA — Startup Items",
      [UserRoleEnum.SPONSOR]: "Sponsor — Read Only",
      [UserRoleEnum.PRINCIPAL_INVESTIGATOR]: "PI — Site View",
      [UserRoleEnum.STUDY_COORDINATOR]: "Coordinator — Site View",
      [UserRoleEnum.TENANT]: "Tenant Admin — No Access",
      [UserRoleEnum.SITE_ADMIN]: "Site Admin — Site Management",
      [UserRoleEnum.ACCOUNTANT]: "Accountant — Finance",
      [UserRoleEnum.FM]: "Finance Manager — Finance",
      [UserRoleEnum.REGULATORY]: "Regulatory — No QMS Access",
    };
    return labels[normalizedRole] ?? normalizedRole;
  },
};

// ─── Type Guards ─────────────────────────────────────────────────────────────

export function isQMSRecordType(value: string): value is QMSRecordType {
  return QMS_RECORD_TYPES.includes(value as QMSRecordType);
}

export function isQMSSeverity(value: string): value is QMSSeverity {
  return QMS_SEVERITIES.includes(value as QMSSeverity);
}

export function isQMSStatus(value: string): value is QMSStatus {
  return QMS_STATUS_VALUES.includes(value as QMSStatus);
}

export function isQMSSource(value: string): value is QMSSource {
  return QMS_SOURCES.includes(value as QMSSource);
}
